import { Map } from 'mapbox-gl';
import { IControl } from 'react-map-gl';
import MapboxDraw, { MapboxDrawOptions } from '@mapbox/mapbox-gl-draw';
import classNames from 'classnames';

import { Nullable } from '~globals/types';

export type MapControlBaseEventHandler<E = MouseEvent> = (
  event: E,
  drawInstance: Nullable<MapControlBar>,
) => void;

export interface MapControlCustomCoreButton {
  selector: string;
  title: string;
}

export interface MapControlCustomBarButton {
  className: string;
  title: string;
  eventHandler: MapControlBaseEventHandler;
}

export interface MapControlBarButtons {
  core?: MapControlCustomCoreButton[];
  customs?: MapControlCustomBarButton[];
}

class MapControlBar implements IControl {
  map: Nullable<Map>;

  container: Nullable<HTMLElement>;

  ctrl: MapboxDraw;

  buttons: MapControlBarButtons;

  constructor(options?: MapboxDrawOptions, buttons?: MapControlBarButtons) {
    this.map = null;
    this.container = null;
    this.ctrl = new MapboxDraw(options);
    this.buttons = buttons ?? { core: [], customs: [] };
  }

  private setCoreButton(option: MapControlCustomCoreButton) {
    this.container
      ?.querySelector(option.selector)
      ?.setAttribute('title', option.title);
  }

  private addCustomButton(option: MapControlCustomBarButton) {
    const elButton = document.createElement('button');

    elButton.className = classNames(
      'mapbox-gl-draw_ctrl-draw-btn',
      option.className,
    );
    elButton.type = 'button';
    elButton.title = option.title;
    elButton.onclick = (event) => option.eventHandler(event, this);

    this.container?.appendChild(elButton);
  }

  onAdd(map: Map): HTMLElement {
    this.map = map;
    this.container = this.ctrl.onAdd(map);

    this.buttons.core?.forEach((button) => {
      this.setCoreButton(button);
    });

    this.buttons.customs?.forEach((button) => {
      this.addCustomButton(button);
    });

    return this.container;
  }

  onRemove(map: Map): void {
    this.container?.parentNode?.removeChild(this.container);
    this.map = null;
    this.ctrl.onRemove(map);
  }

  activedButton(selector: string): void {
    this.container?.querySelector(selector)?.classList.add('active');
  }

  desactivedButton(selector: string): void {
    this.container?.querySelector(selector)?.classList.remove('active');
  }

  toggleActiveButton(selector: string): void {
    this.container?.querySelector(selector)?.classList.toggle('active');
  }

  disabledButton(selector: string): void {
    this.container?.querySelector(selector)?.setAttribute('disabled', '');
  }

  enabledButton(selector: string): void {
    this.container?.querySelector(selector)?.removeAttribute('disabled');
  }

  toggleDisabledButton(selector: string): void {
    this.container?.querySelector(selector)?.toggleAttribute('disabled');
  }
}

export default MapControlBar;
